import type { StreamingEvent } from '@circuit/chat-utils';
import type { ChatWSClient, ChatWSTurnHandler } from '@circuit/chat/streaming';

import { ChatWSClient as ChatWSClientImpl } from '@circuit/chat/streaming';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { readSessionToken, writeSessionToken } from '../sessionToken';
import { AssistantStreamMessage, Message } from '../types/message.types';

const _rawApiEndpoint = import.meta.env.CIRCUIT_API_ENDPOINT ?? '';
const API_ENDPOINT = _rawApiEndpoint === 'undefined' ? '' : _rawApiEndpoint;

const GENERIC_ERROR_MESSAGE = 'Sorry, something went wrong.';

/**
 * Derive the WS URL for `/chat-bot/ws` from the configured
 * HTTP endpoint. Empty API_ENDPOINT resolves against `window.location`.
 */
function getChatBotWSURL(): string {
  const swap = (endpoint: string): string => {
    if (endpoint.startsWith('https://')) {
      return `wss://${endpoint.slice('https://'.length)}`;
    }
    if (endpoint.startsWith('http://')) {
      return `ws://${endpoint.slice('http://'.length)}`;
    }
    return endpoint;
  };
  const derived = swap(API_ENDPOINT);
  if (derived.length > 0) return `${derived}/chat-bot/ws`;
  if (typeof window === 'undefined') return '/chat-bot/ws';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/chat-bot/ws`;
}

interface UseChatStreamOptions {
  apiKey: string;
  setMessages: (updater: (prev: Message[]) => Message[]) => void;
  onSessionReset?: () => void;
}

interface ActiveTurn {
  ignoreAfterAbort: { value: boolean };
  settle: () => void;
}

/**
 * Preact widget WS adapter over @circuit/chat/streaming.
 *
 * Wire protocol identical to the embed:
 *   1. Open WS to `/chat-bot/ws`.
 *   2. First frame `{type:'auth', token:apiKey, session_token?}`.
 *   3. Server responds `auth_ok` with `chat_id` + `session_token`; we
 *      persist the token to localStorage for the next visit.
 *   4. `submit` frames start turns; the turn handler dispatches events.
 *
 * Preserves the widget's existing return shape: `{ input, setInput,
 * isLoading, progressText, handleSend }`. UI stays untouched.
 */
export function useChatStream({
  apiKey,
  setMessages,
  onSessionReset,
}: UseChatStreamOptions) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressText, setProgressText] = useState('');

  const clientRef = useRef<ChatWSClient | null>(null);
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const apiKeyRef = useRef(apiKey);
  apiKeyRef.current = apiKey;
  const onSessionResetRef = useRef(onSessionReset);
  onSessionResetRef.current = onSessionReset;
  const activeTurnRef = useRef<ActiveTurn | null>(null);

  const updateBotMessage = useCallback(
    (events: StreamingEvent[]): void => {
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        const next: AssistantStreamMessage = {
          events: [...events],
          sender: 'bot',
        };
        if (lastMessage?.sender === 'bot') {
          return [...prev.slice(0, -1), next];
        }
        return [...prev, next];
      });
    },
    [setMessages],
  );

  const ensureClient = useCallback((): ChatWSClient => {
    if (clientRef.current) return clientRef.current;
    const client = new ChatWSClientImpl({
      url: getChatBotWSURL(),
      getAccessToken: () => apiKeyRef.current,
      getSessionToken: () => readSessionToken(apiKeyRef.current),
      onSessionToken: (token) => {
        writeSessionToken(apiKeyRef.current, token);
      },
      onChatIDChanged: () => {
        if (clientRef.current !== client) return;
        const turn = activeTurnRef.current;
        if (turn) {
          turn.ignoreAfterAbort.value = true;
          turn.settle();
        }
        clientRef.current = null;
        onSessionResetRef.current?.();
      },
      logger: {
        error: (entry) => {
          console.error('[circuit-chat-widget]', entry.message, entry.error);
        },
        warn: (entry) => {
          console.warn('[circuit-chat-widget]', entry.message);
        },
      },
    });
    clientRef.current = client;
    return client;
  }, []);

  // Disconnect the socket on unmount.
  useEffect(() => {
    return () => {
      clientRef.current?.disconnect();
      clientRef.current = null;
    };
  }, []);

  const surfaceError = useCallback(
    (message: string, streamed: StreamingEvent[]): void => {
      const errorEvent: StreamingEvent<'error'> = {
        data: { message },
        event: 'error',
      };
      if (streamed.length > 0) {
        streamed.push(errorEvent);
        updateBotMessage(streamed);
        return;
      }
      setMessages((prev) => [...prev, { events: [errorEvent], sender: 'bot' }]);
    },
    [setMessages, updateBotMessage],
  );

  const handleSend = useCallback(async (): Promise<void> => {
    const trimmed = input.trim();
    if (trimmed === '' || isLoadingRef.current) return;
    const client = ensureClient();
    if (client.hasActiveTurn()) return;

    setMessages((prev) => [...prev, { sender: 'user', text: trimmed }]);
    setInput('');
    setIsLoading(true);

    const streamed: StreamingEvent[] = [];
    const ignoreAfterAbort = { value: false };

    await new Promise<void>((resolve) => {
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        activeTurnRef.current = null;
        client.setTurnHandler(null);
        setIsLoading(false);
        setProgressText('');
        resolve();
      };
      activeTurnRef.current = { ignoreAfterAbort, settle };

      const handler: ChatWSTurnHandler = {
        ignoreAfterAbort,
        onEvent: (event: StreamingEvent) => {
          if (ignoreAfterAbort.value) return;
          if (event.event === 'start') return;
          if (event.event === 'progress') {
            setProgressText((event as StreamingEvent<'progress'>).data);
            return;
          }
          if ((event.event as string) === 'done') {
            setProgressText('');
            if (streamed.length === 0) {
              surfaceError(GENERIC_ERROR_MESSAGE, streamed);
            }
            settle();
            return;
          }
          if (event.event === 'canceled' || event.event === 'paused') {
            streamed.push(event);
            updateBotMessage(streamed);
            settle();
            return;
          }
          if (event.event === 'error') {
            streamed.push(event);
            updateBotMessage(streamed);
            settle();
            return;
          }
          if (event.event === 'text') setProgressText('');
          streamed.push(event);
          updateBotMessage(streamed);
        },
        onError: (err: Error) => {
          if (ignoreAfterAbort.value) return;
          surfaceError(err.message || GENERIC_ERROR_MESSAGE, streamed);
          settle();
        },
      };

      client.setTurnHandler(handler);
      client
        .submit({
          messages: [{ block: { value: trimmed }, type: 'text' }],
          source: 'user',
        })
        .catch((err: unknown) => {
          if (ignoreAfterAbort.value) return;
          const msg =
            err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE;
          surfaceError(msg, streamed);
          settle();
        });
    });
  }, [ensureClient, input, setMessages, surfaceError, updateBotMessage]);

  return { input, setInput, isLoading, progressText, handleSend };
}
